import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';

const FRONTEND_STACK_OPTIONS = [
  { value: 'react', label: 'React' },
  { value: 'vue', label: 'Vue' },
  { value: 'angular', label: 'Angular' },
  { value: 'svelte', label: 'Svelte' },
  { value: 'pure_js', label: 'Pure JavaScript' },
];

const BACKEND_STACK_OPTIONS = [
  { value: 'laravel', label: 'Laravel' },
  { value: 'node', label: 'Node.js' },
  { value: 'pure_php', label: 'Pure PHP' },
  { value: 'python', label: 'Python' },
];

function getAiGuideFilename(appName) {
  const slug = appName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${slug || 'application'}-ai-implementation-guide.md`;
}

function getCredentialsFilename(appName) {
  return `${appName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-credentials.json`;
}

function WizardQuestion({ number, title, children }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 dark:border-slate-700 dark:bg-slate-900 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
    >
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
          {number}
        </span>
        <h3 className="pt-0.5 text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function ChoiceButton({ selected, children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors disabled:cursor-wait disabled:opacity-60 ${
        selected
          ? 'border-indigo-500 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-200'
          : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-indigo-500'
      }`}
    >
      {children}
    </button>
  );
}

function renderInline(text, keyPrefix) {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={`${keyPrefix}-${index}`} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.9em] dark:bg-slate-800">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${index}`}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function MarkdownDocument({ markdown }) {
  const lines = markdown.split(/\r?\n/);
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const codeStart = line.match(/^```([a-zA-Z0-9_-]*)\s*$/);
    if (codeStart) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index])) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push({ type: 'code', language: codeStart[1], content: codeLines.join('\n') });
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: `h${heading[1].length}`, content: heading[2] });
      index += 1;
      continue;
    }

    const listMatch = line.match(/^\s*([-*]|\d+\.)\s+(.+)$/);
    if (listMatch) {
      const ordered = /^\d/.test(listMatch[1]);
      const items = [];
      while (index < lines.length) {
        const item = lines[index].match(/^\s*([-*]|\d+\.)\s+(.+)$/);
        if (!item || ordered !== /^\d/.test(item[1])) break;
        items.push(item[2]);
        index += 1;
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      continue;
    }

    const paragraph = [line.trim()];
    index += 1;
    while (
      index < lines.length
      && lines[index].trim()
      && !/^(#{1,3})\s|^```/.test(lines[index])
      && !/^\s*([-*]|\d+\.)\s+/.test(lines[index])
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ type: 'p', content: paragraph.join(' ') });
  }

  return (
    <div className="space-y-4 text-sm leading-7 text-slate-700 dark:text-slate-300">
      {blocks.map((block, blockIndex) => {
        const key = `markdown-${blockIndex}`;
        if (block.type === 'code') {
          return (
            <div key={key} className="overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
              {block.language && (
                <div className="border-b border-slate-800 px-4 py-2 text-xs text-slate-400">{block.language}</div>
              )}
              <pre className="overflow-x-auto p-4 text-xs leading-5 text-slate-100">
                <code>{block.content}</code>
              </pre>
            </div>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const List = block.type;
          return (
            <List key={key} className={`${block.type === 'ul' ? 'list-disc' : 'list-decimal'} space-y-1 pl-6`}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
              ))}
            </List>
          );
        }
        if (block.type.startsWith('h')) {
          const Heading = block.type;
          return (
            <Heading key={key} className="pt-2 text-base font-semibold text-slate-900 dark:text-white">
              {renderInline(block.content, key)}
            </Heading>
          );
        }
        return <p key={key}>{renderInline(block.content, key)}</p>;
      })}
    </div>
  );
}

export function IntegrationSdkDocs() {
  const { accountIndex = '0' } = useParams();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [hasPending, setHasPending] = useState(false);
  const [hasApplications, setHasApplications] = useState(false);
  const [productName, setProductName] = useState('');
  const [selectedAppId, setSelectedAppId] = useState('');
  const [integrationType, setIntegrationType] = useState('');
  const [projectState, setProjectState] = useState('');
  const [architecture, setArchitecture] = useState('');
  const [frontendStack, setFrontendStack] = useState('');
  const [backendStack, setBackendStack] = useState('');
  const [aiStep, setAiStep] = useState(0);
  const [aiInstructionDownloaded, setAiInstructionDownloaded] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingApps, setLoadingApps] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const generationController = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    api.get('/developers/docs/apps', { signal: controller.signal })
      .then(({ data }) => {
        if (
          !Array.isArray(data.apps)
          || !data.apps.every((app) => (typeof app.id === 'number' || typeof app.id === 'string') && typeof app.name === 'string')
          || typeof data.has_pending !== 'boolean'
          || typeof data.has_applications !== 'boolean'
          || typeof data.product_name !== 'string'
        ) {
          throw new Error('The approved applications service returned an invalid response.');
        }
        setApps(data.apps);
        setHasPending(data.has_pending);
        setHasApplications(data.has_applications);
        setProductName(data.product_name);
      })
      .catch((requestError) => {
        if (requestError.name !== 'CanceledError' && requestError.code !== 'ERR_CANCELED') {
          console.error('Failed to load approved applications for integration docs', requestError);
          setError('Unable to load your approved applications. Please try again.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingApps(false);
      });

    return () => {
      controller.abort();
      generationController.current?.abort();
    };
  }, []);

  const generate = async (answers, { download = false } = {}) => {
    generationController.current?.abort();
    const controller = new AbortController();
    generationController.current = controller;
    setGenerating(true);
    setError('');
    setResult(null);

    try {
      const response = await api.post('/developers/docs/generate', answers, {
        signal: controller.signal,
      });
      if (
        typeof response.data?.title !== 'string'
        || typeof response.data?.filename !== 'string'
        || typeof response.data?.markdown !== 'string'
      ) {
        throw new Error('The documentation service returned an invalid response.');
      }
      if (download) {
        const blob = new Blob([response.data.markdown], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = response.data.filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
        setAiInstructionDownloaded(true);
      } else {
        setResult(response.data);
      }
    } catch (requestError) {
      if (requestError.name !== 'CanceledError' && requestError.code !== 'ERR_CANCELED') {
        console.error('Failed to generate integration documentation', requestError);
        setError(requestError.response?.data?.message || requestError.message || 'Unable to generate the integration guide. Please try again.');
      }
    } finally {
      if (!controller.signal.aborted) setGenerating(false);
    }
  };

  const handleAppChange = (event) => {
    generationController.current?.abort();
    setSelectedAppId(event.target.value);
    setIntegrationType('');
    setProjectState('');
    setArchitecture('');
    setFrontendStack('');
    setBackendStack('');
    setAiStep(0);
    setAiInstructionDownloaded(false);
    setPromptCopied(false);
    setResult(null);
    setError('');
    setGenerating(false);
  };

  const chooseIntegrationType = (type) => {
    setIntegrationType(type);
    setProjectState('');
    setArchitecture('');
    setFrontendStack('');
    setBackendStack('');
    setAiStep(type === 'ai_agent' ? 1 : 0);
    setAiInstructionDownloaded(false);
    setPromptCopied(false);
    setResult(null);
  };

  const chooseProjectState = (state) => {
    setProjectState(state);
    setArchitecture('');
    setFrontendStack('');
    setBackendStack('');
    setResult(null);
    setError('');
  };

  const chooseArchitecture = (selectedArchitecture) => {
    setArchitecture(selectedArchitecture);
    setFrontendStack('');
    setBackendStack('');
    setResult(null);
    setError('');
  };

  const chooseFrontendStack = (selectedFrontendStack) => {
    setFrontendStack(selectedFrontendStack);
    setBackendStack('');
    setResult(null);
    setError('');
  };

  const chooseBackendStack = (selectedBackendStack) => {
    setBackendStack(selectedBackendStack);
    generate({
      app_id: selectedAppId,
      integration_type: 'myself',
      project_state: projectState,
      architecture,
      frontend_stack: frontendStack,
      backend_stack: selectedBackendStack,
    });
  };

  const downloadAiInstructions = () => {
    setAiInstructionDownloaded(false);
    generate(
      { app_id: selectedAppId, integration_type: 'ai_agent' },
      { download: true }
    );
  };

  const copyAiPrompt = async () => {
    if (!selectedApp) return;

    const prompt = `Locate the downloaded \`${getAiGuideFilename(selectedApp.name)}\` and \`${getCredentialsFilename(selectedApp.name)}\` in the root directory. Review \`src/auth/\` (or equivalent) in this repository to understand the current session management. Then, using the specific OIDC/PKCE instructions in the markdown, integrate ${selectedApp.name} with OneLogin. Keep the existing auth strategy as a fallback. Implement step-by-step and write verification tests for the callback route.`;
    try {
      await navigator.clipboard.writeText(prompt);
      setPromptCopied(true);
      setError('');
    } catch (copyError) {
      console.error('Failed to copy the AI implementation prompt', copyError);
      setError('Unable to copy the prompt. Please select and copy the text manually.');
    }
  };

  if (loadingApps) {
    return (
      <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-sm text-slate-500 dark:text-slate-400" role="status">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        Loading approved applications...
      </div>
    );
  }

  if (error && apps.length === 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200" role="alert">
        {error}
      </div>
    );
  }

  if (apps.length === 0) {
    const message = hasPending
      ? 'Hi Developer, your application is currently under review. Please return to this documentation once your system is approved. You will be notified via email/phone. You can track the status in your App Workspace.'
      : !hasApplications
        ? "Hi Developer, you haven't registered any applications yet. Please go to your App Workspace to register your first app. 💡 Note on Callback URLs: When registering, carefully provide your Callback Path. This is the exact URL where our system will redirect your users after a successful login. We strictly validate this against your app's origin for security."
        : 'No approved applications are currently available for documentation. Please review your application status in the App Workspace.';

    return (
      <div className="mx-auto max-w-3xl rounded-xl border border-indigo-100 bg-indigo-50/70 p-6 dark:border-indigo-900/60 dark:bg-indigo-950/30">
        <p className="text-sm leading-7 text-slate-700 dark:text-slate-200">{message}</p>
        <button
          type="button"
          onClick={() => navigate(`/u/${accountIndex}/dashboard/console/workspace`)}
          className="mt-5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
        >
          Open App Workspace
        </button>
      </div>
    );
  }

  const selectedApp = apps.find((app) => String(app.id) === String(selectedAppId));
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-5 dark:border-indigo-900/60 dark:bg-indigo-950/30">
        <p className="text-sm leading-6 text-indigo-950 dark:text-indigo-100">
          Answer a few questions and we’ll prepare an integration guide tailored to your application.
        </p>
      </div>

      <WizardQuestion number="1" title={`Which approved application would you like to connect to ${productName}?`}>
        <label htmlFor="integration-app" className="mb-2 block text-sm text-slate-600 dark:text-slate-400">
          Approved application
        </label>
        <select
          id="integration-app"
          value={selectedAppId}
          onChange={handleAppChange}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="">Choose an application</option>
          {apps.map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}
        </select>
      </WizardQuestion>

      {selectedApp && (
        <WizardQuestion number="2" title="How would you like to build the integration?">
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceButton selected={integrationType === 'ai_agent'} disabled={generating} onClick={() => chooseIntegrationType('ai_agent')}>
              Use an AI coding assistant <span className="mt-1 block text-xs font-normal opacity-75">Download a tailored implementation brief and use it with your preferred AI tool.</span>
            </ChoiceButton>
            <ChoiceButton selected={integrationType === 'myself'} disabled={generating} onClick={() => chooseIntegrationType('myself')}>
              Guide me through a manual implementation <span className="mt-1 block text-xs font-normal opacity-75">Get a practical guide tailored to your project and technology stack.</span>
            </ChoiceButton>
          </div>
        </WizardQuestion>
      )}

      {integrationType === 'ai_agent' && aiStep >= 1 && (
        <WizardQuestion number="AI 1" title="Download your AI implementation brief">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={downloadAiInstructions}
              disabled={generating}
              className="rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60"
            >
              {generating ? 'Preparing your brief…' : aiInstructionDownloaded ? 'Download the brief again' : 'Download implementation brief'}
            </button>
            <button
              type="button"
              onClick={() => setAiStep(2)}
              disabled={!aiInstructionDownloaded || generating}
              className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              I’ve saved the brief and credentials
            </button>
          </div>
        </WizardQuestion>
      )}

      {integrationType === 'ai_agent' && aiStep >= 2 && (
        <WizardQuestion number="AI 2" title="Add both files to your project">
          <p className="mb-4 text-sm leading-6 text-slate-600 dark:text-slate-400">
            Place the credentials JSON and downloaded implementation brief in your project folder. Keep the credentials private and out of source control.
          </p>
          <button
            type="button"
            onClick={() => setAiStep(3)}
            className="rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
          >
            Both files are in my project
          </button>
        </WizardQuestion>
      )}

      {integrationType === 'ai_agent' && aiStep >= 3 && (
        <WizardQuestion number="AI 3" title="Share this implementation request with your AI assistant">
          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="select-all text-sm leading-6 text-slate-800 dark:text-slate-200">
              {selectedApp && `Read the downloaded ${getAiGuideFilename(selectedApp.name)} file and use the ${getCredentialsFilename(selectedApp.name)}. First, deeply analyze this host repository's frontend and backend to understand its existing SDK initialization and authentication flow. Then, identify my exact tech stack and architecture. Finally, integrate ${selectedApp.name} with the One Login SSO platform according to your findings. Do not break existing authentication.`}
            </p>
            <button
              type="button"
              onClick={copyAiPrompt}
              className="self-start rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
            >
              {promptCopied ? 'Copied' : 'Copy prompt'}
            </button>
          </div>
        </WizardQuestion>
      )}

      {integrationType === 'myself' && (
        <WizardQuestion number="3" title="Are you starting a new project or updating an existing one?">
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceButton selected={projectState === 'new'} disabled={generating} onClick={() => chooseProjectState('new')}>
              New project <span className="mt-1 block text-xs font-normal opacity-75">Build the integration into a new application.</span>
            </ChoiceButton>
            <ChoiceButton selected={projectState === 'existing'} disabled={generating} onClick={() => chooseProjectState('existing')}>
              Existing project <span className="mt-1 block text-xs font-normal opacity-75">Add sign-in without disrupting your current authentication.</span>
            </ChoiceButton>
          </div>
        </WizardQuestion>
      )}

      {integrationType === 'myself' && projectState && (
        <WizardQuestion number="4" title="How is your application structured?">
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceButton selected={architecture === 'monolithic'} disabled={generating} onClick={() => chooseArchitecture('monolithic')}>
              Monolithic application <span className="mt-1 block text-xs font-normal opacity-75">Frontend and backend are part of one application.</span>
            </ChoiceButton>
            <ChoiceButton selected={architecture === 'decoupled'} disabled={generating} onClick={() => chooseArchitecture('decoupled')}>
              Decoupled application <span className="mt-1 block text-xs font-normal opacity-75">Frontend and backend are separate applications or services.</span>
            </ChoiceButton>
          </div>
        </WizardQuestion>
      )}

      {integrationType === 'myself' && projectState && architecture && (
        <WizardQuestion number="5" title="Select your frontend stack">
          <div className="grid gap-3 sm:grid-cols-2">
            {FRONTEND_STACK_OPTIONS.map((option) => (
              <ChoiceButton
                key={option.value}
                selected={frontendStack === option.value}
                disabled={generating}
                onClick={() => chooseFrontendStack(option.value)}
              >
                {option.label}
              </ChoiceButton>
            ))}
          </div>
        </WizardQuestion>
      )}

      {integrationType === 'myself' && projectState && architecture && frontendStack && (
        <WizardQuestion number="6" title="Select your backend stack">
          <div className="grid gap-3 sm:grid-cols-2">
            {BACKEND_STACK_OPTIONS.map((option) => (
              <ChoiceButton
                key={option.value}
                selected={backendStack === option.value}
                disabled={generating}
                onClick={() => chooseBackendStack(option.value)}
              >
                {option.label}
              </ChoiceButton>
            ))}
          </div>
        </WizardQuestion>
      )}

      {generating && (
        <div className="flex items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-800 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200" role="status">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          Preparing your personalized integration guide...
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200" role="alert">
          {error}
        </div>
      )}

      {result && !generating && (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <div>
              <h2 className="font-semibold text-slate-900 dark:text-white">{result.title}</h2>
              {selectedApp && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{selectedApp.name}</p>}
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <MarkdownDocument markdown={result.markdown} />
          </div>
        </section>
      )}
    </div>
  );
}

export default IntegrationSdkDocs;
